import { RateLimited } from '@/lib/errors';
import type { EhHost } from './errors';

export type EhTransport = 'api' | 'proxy';

interface Cooldown {
  retryAt: number;
  retryAfterText?: string;
}

interface PendingPermit {
  site: EhHost;
  transport: EhTransport;
  resolve: (coordinated: boolean) => void;
  reject: (error: Error) => void;
}
interface Coordinator {
  worker: SharedWorker;
  port: MessagePort;
}

interface PermitMessage {
  type: 'permit';
  requestId: number;
}

interface CooldownMessage {
  type: 'cooldown';
  requestId: number;
  retryAt: number;
  retryAfterText?: string;
}

type CoordinatorMessage = PermitMessage | CooldownMessage;

const localCooldowns = new Map<string, Cooldown>();
const pendingPermits = new Map<number, PendingPermit>();
let coordinator: Coordinator | null | undefined;
let nextRequestId = 1;

function cooldownKey(site: EhHost, transport: EhTransport): string {
  return `${site}:${transport}`;
}

function activeCooldown(site: EhHost, transport: EhTransport): Cooldown | undefined {
  const key = cooldownKey(site, transport);
  const cooldown = localCooldowns.get(key);
  if (!cooldown) return undefined;
  if (cooldown.retryAt <= Date.now()) {
    localCooldowns.delete(key);
    return undefined;
  }
  return cooldown;
}

function cooldownError(site: EhHost, transport: EhTransport, cooldown: Cooldown): RateLimited {
  return new RateLimited(
    `Requests to ${site} are paused until the current rate-limit window expires`,
    {
      source: site,
      egress: transport === 'api' ? 'client' : 'proxy',
      retryAt: cooldown.retryAt,
      retryAfterText: cooldown.retryAfterText,
    },
  );
}

function isCoordinatorMessage(value: unknown): value is CoordinatorMessage {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('type' in value) ||
    !('requestId' in value)
  ) {
    return false;
  }
  if (typeof value.requestId !== 'number') return false;
  if (value.type === 'permit') return true;
  if (value.type !== 'cooldown' || !('retryAt' in value) || typeof value.retryAt !== 'number') {
    return false;
  }
  return !('retryAfterText' in value) || typeof value.retryAfterText === 'string';
}

function disableCoordinator(): void {
  coordinator = null;
  for (const pending of pendingPermits.values()) {
    pending.resolve(false);
  }
  pendingPermits.clear();
}

function getCoordinatorPort(): MessagePort | null {
  if (coordinator !== undefined) return coordinator?.port ?? null;
  if (typeof SharedWorker !== 'function') {
    coordinator = null;
    return null;
  }

  try {
    const worker = new SharedWorker(
      new URL('./request-control.shared-worker.ts', import.meta.url),
      {
        name: 'bunko-eh-request-control',
        type: 'module',
      },
    );
    worker.port.addEventListener('message', (event: MessageEvent<unknown>) => {
      if (!isCoordinatorMessage(event.data)) return;
      const pending = pendingPermits.get(event.data.requestId);
      if (!pending) return;
      pendingPermits.delete(event.data.requestId);
      if (event.data.type === 'permit') {
        pending.resolve(true);
        return;
      }
      const cooldown: Cooldown = {
        retryAt: event.data.retryAt,
        ...(event.data.retryAfterText !== undefined
          ? { retryAfterText: event.data.retryAfterText }
          : {}),
      };
      localCooldowns.set(cooldownKey(pending.site, pending.transport), cooldown);
      pending.reject(cooldownError(pending.site, pending.transport, cooldown));
    });
    worker.port.addEventListener('messageerror', disableCoordinator);
    worker.addEventListener('error', disableCoordinator);
    worker.port.start();
    coordinator = { worker, port: worker.port };
  } catch {
    disableCoordinator();
  }
  return coordinator?.port ?? null;
}

export async function waitForEhRequest(
  site: EhHost,
  transport: EhTransport,
  intervalMs: number,
): Promise<boolean> {
  const cooldown = activeCooldown(site, transport);
  if (cooldown) throw cooldownError(site, transport, cooldown);

  const port = getCoordinatorPort();
  if (!port) return false;

  const requestId = nextRequestId;
  nextRequestId += 1;
  const permit = new Promise<boolean>((resolve, reject) => {
    pendingPermits.set(requestId, { site, transport, resolve, reject });
  });
  try {
    port.postMessage({ type: 'request', requestId, site, transport, intervalMs });
  } catch {
    disableCoordinator();
    return false;
  }
  return permit;
}

export function reportEhRateLimit(site: EhHost, transport: EhTransport, error: RateLimited): void {
  if (error.retryAt === undefined || error.retryAt <= Date.now()) return;
  const cooldown: Cooldown = {
    retryAt: error.retryAt,
    ...(error.retryAfterText !== undefined ? { retryAfterText: error.retryAfterText } : {}),
  };
  localCooldowns.set(cooldownKey(site, transport), cooldown);
  getCoordinatorPort()?.postMessage({
    type: 'set-cooldown',
    site,
    transport,
    retryAt: cooldown.retryAt,
    ...(cooldown.retryAfterText !== undefined ? { retryAfterText: cooldown.retryAfterText } : {}),
  });
}

export function rateLimitForTransport(error: RateLimited, transport: EhTransport): RateLimited {
  const egress = transport === 'api' ? 'client' : 'proxy';
  if (error.egress === egress) return error;
  return new RateLimited(error.message, {
    source: error.source,
    egress,
    retryAt: error.retryAt,
    retryAfterText: error.retryAfterText,
    cause: error,
  });
}
